import { Bell, BellOff, BellRing, MessageSquarePlus, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useWhatsAppUnread } from '../../context/WhatsAppUnreadContext';
import ContactPanel from './ContactPanel';
import ConversationList from './ConversationList';
import ConversationThread from './ConversationThread';
import NewChatDialog from './NewChatDialog';
import useInbox from './useInbox';

/**
 * The Inbox tab: a WhatsApp-Web-style conversation list, an open thread with its composer, and a
 * contact panel. All state and polling live in `useInbox`; this component only wires it to the
 * three presentational panes and the narrow-screen list/thread/contact-panel collapse.
 * The contact panel is hidden until the Info button is pressed.
 * @param {{onOpenNewCampaign?: () => void, initialPhone?: string, linkKey?: string}} props `initialPhone` opens that chat (from ?phone=)
 */
const InboxTab = ({ onOpenNewCampaign, initialPhone, linkKey }) => {
  const inbox = useInbox();
  const unread = useWhatsAppUnread();
  const { setActivePhone } = unread;
  const openedFromLink = useRef(null);
  const { selectConversation } = inbox;

  // Tell the alert provider which chat is open, so it stays quiet about that one only.
  useEffect(() => {
    setActivePhone(inbox.selectedPhone);
    return () => setActivePhone(null);
  }, [inbox.selectedPhone, setActivePhone]);

  // Open the chat named by ?phone= (a toast / notification click-through), once per distinct value.
  useEffect(() => {
    if (initialPhone && openedFromLink.current !== `${initialPhone}|${linkKey ?? ''}`) {
      openedFromLink.current = `${initialPhone}|${linkKey ?? ''}`;
      selectConversation(initialPhone);
    }
  }, [initialPhone, linkKey, selectConversation]);
  const [showContact, setShowContact] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);

  return (
    <div className="relative flex h-full min-h-[480px] w-full max-w-full overflow-hidden rounded-lg border border-border bg-card">
      <div
        className={`w-full flex-col border-r border-border lg:flex lg:w-[320px] lg:flex-none ${
          inbox.selectedPhone ? 'hidden' : 'flex'
        }`}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <h2 className="text-sm font-medium text-foreground">Chats</h2>
          <button
            type="button"
            onClick={() => unread.setMuted(!unread.muted)}
            aria-label={unread.muted ? 'Unmute new-message sound' : 'Mute new-message sound'}
            aria-pressed={unread.muted}
            className="ml-auto rounded p-2 hover:bg-gray-100"
          >
            {unread.muted ? <BellOff size={16} /> : <Bell size={16} />}
          </button>
          {unread.desktopPermission === 'default' && (
            <button
              type="button"
              onClick={unread.requestDesktopPermission}
              aria-label="Enable desktop alerts"
              title="Enable desktop alerts"
              className="rounded p-2 hover:bg-gray-100"
            >
              <BellRing size={16} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setNewChatOpen(true)}
            aria-label="New chat"
            className="rounded-lg p-2 hover:bg-accent"
          >
            <MessageSquarePlus size={16} />
          </button>
          <button
            type="button"
            onClick={inbox.refresh}
            aria-label="Refresh inbox"
            disabled={inbox.convLoading}
            className="rounded-lg p-2 hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw size={16} className={inbox.convLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <ConversationList
          conversations={inbox.conversations}
          loaded={inbox.conversationsLoaded}
          loading={inbox.convLoading}
          error={inbox.convError}
          onRetry={inbox.retryConversations}
          selectedPhone={inbox.selectedPhone}
          onSelect={inbox.selectConversation}
          hasMore={inbox.hasMoreConversations}
          loadingMore={inbox.loadingMoreConversations}
          onLoadMore={inbox.loadMoreConversations}
        />
      </div>

      <div className={`min-w-0 flex-1 flex-col lg:flex ${inbox.selectedPhone ? 'flex' : 'hidden'}`}>
        <ConversationThread
          // Remounts the whole pane (including the composer) on every conversation switch, so a
          // draft typed for one customer can never survive into another's box (nor can any other
          // per-conversation UI state hidden inside this subtree).
          key={inbox.selectedPhone}
          phone={inbox.selectedPhone}
          contact={inbox.thread?.contact}
          wholesalePartner={inbox.thread?.wholesalePartner}
          messages={inbox.thread?.messages || []}
          windowOpen={!!inbox.thread?.windowOpen}
          suppressed={!!inbox.thread?.suppressed}
          sendDisabled={inbox.sendDisabled}
          loading={inbox.threadLoading}
          error={inbox.threadError}
          onRetry={inbox.retryThread}
          hasMoreMessages={inbox.hasMoreMessages}
          loadingEarlier={inbox.loadingEarlier}
          onLoadEarlier={inbox.loadEarlierMessages}
          onSend={inbox.sendReply}
          onSendMedia={inbox.sendMedia}
          onSendReaction={inbox.sendReaction}
          onSendInteractive={inbox.sendInteractive}
          onSendCatalog={inbox.sendCatalog}
          onSendTemplate={inbox.sendTemplate}
          onEditMessage={inbox.editMessage}
          onDeleteMessage={inbox.deleteMessage}
          onFetchMedia={inbox.fetchMedia}
          autoOpenTemplates={inbox.startedChat?.phone === inbox.selectedPhone}
          startedName={inbox.startedChat?.phone === inbox.selectedPhone ? inbox.startedChat?.name : ''}
          onBack={inbox.closeThread}
          onToggleContact={() => setShowContact((v) => !v)}
          onOpenNewCampaign={onOpenNewCampaign}
        />
      </div>

      {newChatOpen && (
        <NewChatDialog
          onClose={() => setNewChatOpen(false)}
          onStart={(phone, name) => {
            setNewChatOpen(false);
            inbox.startChat(phone, name);
          }}
        />
      )}

      {inbox.selectedPhone && showContact && (
        <>
          <div
            data-testid="contact-backdrop"
            className="absolute inset-0 z-10 bg-black/20 xl:hidden"
            onClick={() => setShowContact(false)}
          />
          <aside
            aria-label="Contact details"
            className="absolute inset-y-0 right-0 z-20 flex w-[320px] max-w-full border-l border-border bg-card shadow-xl xl:static xl:flex-none xl:shadow-none"
          >
            <ContactPanel
              contact={inbox.thread?.contact}
              phone={inbox.selectedPhone}
              suppressed={!!inbox.thread?.suppressed}
              onClose={() => setShowContact(false)}
            />
          </aside>
        </>
      )}
    </div>
  );
};

export default InboxTab;
